import { useEffect } from 'react';

// position:fixed is anchored to the nearest ancestor that establishes a containing block
// (any ancestor with a transform/filter/perspective/will-change), not the viewport. When that
// happens the pinned toolbar drifts out of alignment. Walk up and return the offending ancestor
// (if any) so we can surface it rather than silently mis-positioning.
const findFixedContainingBlock = (el) => {
  let node = el && el.parentElement;
  while (node && node !== document.documentElement) {
    const style = window.getComputedStyle(node);
    if ((style.transform && style.transform !== 'none')
      || (style.perspective && style.perspective !== 'none')
      || (style.filter && style.filter !== 'none')
      || (style.willChange && /transform|perspective|filter/.test(style.willChange))) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
};

// The page itself does not scroll (html/body/main are overflow:hidden); a container inside
// <main> does. The toolbar must pin to the bottom of the layout <header> that sits above <main>,
// not the top of the viewport. Every player layout (ActivityMode, Preview, ReviewMode, ScoreMode,
// ItemPreview) renders `<header> ... </header><main> ... </main>` as siblings, so we resolve the
// header relative to the stage's own <main>. This keeps each instance bound to its own layout and
// covers all layouts from one place.
const resolveLayoutHeader = (stage) => {
  const main = stage.closest('main');
  if (!main || !main.parentElement) {
    return null;
  }
  return main.parentElement.querySelector(':scope > header');
};

const clearStuckStyles = (toolbar, placeholder) => {
  placeholder.style.display = 'none';
  toolbar.style.position = '';
  toolbar.style.top = '';
  toolbar.style.left = '';
  toolbar.style.width = '';
};

/**
 * Pins a single draw toolbar just under the layout header while its stage is scrolled past the
 * header's bottom edge, but keeps it bounded to the stage so it rides away with the stage's
 * bottom edge - reproducing `position: sticky` without anchoring to an `overflow` ancestor we
 * cannot change.
 *
 * Resolves its own elements by id (suffixed with `lessonElementId`) so multiple stages can be
 * on screen at once without interfering with one another.
 */
const useBoundedStickyToolbar = (lessonElementId) => {
  useEffect(() => {
    const stage = document.getElementById(`draw-overlay-stage-${lessonElementId}`);
    const toolbar = document.getElementById(`drawToolbar-${lessonElementId}`);
    const placeholder = document.getElementById(`drawToolbarPlaceholder-${lessonElementId}`);

    if (!stage || !toolbar || !placeholder) {
      return undefined;
    }

    const trap = findFixedContainingBlock(stage);
    if (trap) {
      console.warn('[useBoundedStickyToolbar] An ancestor establishes a containing block for '
        + 'position:fixed (transform/filter/perspective), so the pinned toolbar may be '
        + 'misaligned. Ancestor:', trap);
    }

    const header = resolveLayoutHeader(stage);

    let ticking = false;

    const update = () => {
      const rect = stage.getBoundingClientRect();
      const height = toolbar.offsetHeight;
      // The stop point is the bottom edge of the layout header (clamped to >= 0 in case the
      // header is ever scrolled out of view), falling back to the viewport top if no header.
      const offset = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;

      if (rect.top >= offset) {
        // Stage top is still below the header: leave the toolbar in normal flow at the top of
        // the stage.
        clearStuckStyles(toolbar, placeholder);
        return;
      }

      // Stage top has scrolled above the header's bottom: pin the toolbar just under the header.
      // The placeholder holds the toolbar's place in flow so content below does not jump.
      // `Math.min(offset, rect.bottom - height)` keeps it at the header's bottom normally and
      // lets it slide away with the stage's bottom edge.
      placeholder.style.display = 'block';
      placeholder.style.height = `${height}px`;
      toolbar.style.position = 'fixed';
      toolbar.style.top = `${Math.min(offset, rect.bottom - height)}px`;
      toolbar.style.left = `${rect.left}px`;
      toolbar.style.width = `${rect.width}px`;
    };

    const onScrollOrResize = () => {
      if (ticking) {
        return;
      }
      ticking = true;
      window.requestAnimationFrame(() => {
        update();
        ticking = false;
      });
    };

    // capture:true so scrolling inside any nested scroller is caught too.
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);

    // Realign when the stage resizes (e.g. once the canvas finishes its async sizing).
    const resizeObserver = new ResizeObserver(onScrollOrResize);
    resizeObserver.observe(stage);

    update();

    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
      resizeObserver.disconnect();
      clearStuckStyles(toolbar, placeholder);
    };
  }, [lessonElementId]);
};

export default useBoundedStickyToolbar;
